import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { agencyEnablementAPI, levelCapAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

const EMPTY = { carrier_id: '', contract_type_id: '', max_level_id: '', effective_date: '' };

/**
 * EnableCarrierDialog — enables a carrier for an agency and optionally sets a
 * maximum level cap. Owns its own form state and orchestrates both the
 * enablement and (optional) level-cap API calls in sequence.
 */
export const EnableCarrierDialog = ({
  open, onOpenChange, agencyId,
  allCarriers = [], allContractTypes = [], allLevels = [],
  onSaved,
}) => {
  const [form, setForm] = React.useState(EMPTY);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => { if (open) setForm(EMPTY); }, [open]);

  const contractTypes = allContractTypes.filter((ct) => ct.carrier_id === form.carrier_id);
  const levels = allLevels
    .filter((l) => l.carrier_id === form.carrier_id)
    .sort((a, b) => b.level_rank - a.level_rank);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.carrier_id) { toast.error('Please select a carrier'); return; }
    setSaving(true);
    try {
      await agencyEnablementAPI.create({
        agency_id: agencyId,
        carrier_id: form.carrier_id,
        contract_type_id: form.contract_type_id || null,
        enabled: true,
      });
      if (form.max_level_id) {
        const selected = allLevels.find((l) => l.level_id === form.max_level_id);
        if (selected) {
          await levelCapAPI.create({
            agency_id: agencyId,
            carrier_id: form.carrier_id,
            contract_type_id: form.contract_type_id || null,
            max_level_rank: selected.level_rank,
            effective_date: form.effective_date || null,
          });
        }
      }
      toast.success('Carrier enabled for agency');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to enable carrier'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="enable-carrier-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Enable Carrier for Agency</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Carrier *</Label>
              <Select
                value={form.carrier_id}
                onValueChange={(v) => setForm((p) => ({ ...p, carrier_id: v, contract_type_id: '', max_level_id: '' }))}
              >
                <SelectTrigger data-testid="carrier-select"><SelectValue placeholder="Select carrier" /></SelectTrigger>
                <SelectContent>
                  {allCarriers.map((c) => (
                    <SelectItem key={c.carrier_id} value={c.carrier_id}>{c.carrier_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.carrier_id && contractTypes.length > 0 && (
              <div className="space-y-2">
                <Label>Contract Type (Optional)</Label>
                <Select
                  value={form.contract_type_id || '__all__'}
                  onValueChange={(v) => setForm((p) => ({ ...p, contract_type_id: v === '__all__' ? '' : v }))}
                >
                  <SelectTrigger><SelectValue placeholder="All types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All types</SelectItem>
                    {contractTypes.map((ct) => (
                      <SelectItem key={ct.contract_type_id} value={ct.contract_type_id}>{ct.contract_type_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Leave empty to enable all contract types for this carrier.</p>
              </div>
            )}

            {form.carrier_id && (
              <div className="space-y-2">
                <Label>Maximum Level Cap</Label>
                <Select
                  value={form.max_level_id || '__street__'}
                  onValueChange={(v) => setForm((p) => ({ ...p, max_level_id: v === '__street__' ? '' : v }))}
                >
                  <SelectTrigger data-testid="level-cap-select"><SelectValue placeholder="Street Level (default)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__street__">Street Level (default)</SelectItem>
                    {levels.map((l) => (
                      <SelectItem key={l.level_id} value={l.level_id}>{l.level_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Sets the maximum level this agency can request. If not set, only Street Level is allowed.</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Effective Date (Optional)</Label>
              <Input
                type="date"
                value={form.effective_date}
                onChange={(e) => setForm((p) => ({ ...p, effective_date: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-carrier-btn">
              {saving ? 'Saving…' : 'Enable Carrier'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EnableCarrierDialog;
